/**
 * Projects — delivery tracking with budget, members and commits.
 *
 * Fully redesigned: project cards with progress rings and commit
 * sparklines, budget summary table, and a slide-over create drawer with
 * GitHub repo picker. Self-contained styling (dashboard-pages.css).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  FolderGit2,
  FolderPlus,
  GitBranch,
  GitCommitHorizontal,
  GitPullRequest,
  Lock,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import type { Project } from '../types';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

interface GithubRepo {
  id: string;
  fullName: string;
  url: string;
  defaultBranch?: string;
  private?: boolean;
}

const MEMBER_COLORS: Record<string, string> = {
  AK: '#4d9cf8',
  SR: '#f5a623',
  RM: '#f26464',
  PK: '#3ecf8e',
};

const DEMO_TEAM_POOL = [
  { initials: 'AK', name: 'Arjun Kumar' },
  { initials: 'SR', name: 'Sneha Rao' },
  { initials: 'RM', name: 'Rahul Mehta' },
  { initials: 'PK', name: 'Priya Kapoor' },
];

const SPARK_PATTERNS = [
  [35, 55, 45, 70, 60, 85, 100],
  [40, 60, 50, 80, 65, 75, 90],
  [80, 50, 30, 25, 35, 20, 30],
];

function statusText(status: Project['status']) {
  if (status === 'active') return 'Active';
  if (status === 'review') return 'Review';
  if (status === 'paused') return 'Paused';
  return 'Completed';
}

function statusColor(status: Project['status']) {
  if (status === 'active') return T.green;
  if (status === 'review') return T.amber;
  if (status === 'paused') return T.red;
  return T.cyan;
}

function parseRepoFullName(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^https?:\/\/github\.com\//i.test(trimmed)) {
    const afterHost = trimmed.replace(/^https?:\/\/github\.com\//i, '').replace(/\.git$/i, '').replace(/\/$/, '');
    const parts = afterHost.split('/').filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
    return null;
  }
  const normalized = trimmed.replace(/\.git$/i, '').replace(/\/$/, '');
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length === 2) return `${parts[0]}/${parts[1]}`;
  return null;
}

function githubAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('scrudix_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function githubApiGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: 'GET',
    credentials: 'include',
    headers: { ...githubAuthHeaders() },
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `GitHub API request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number; style?: React.CSSProperties; hover?: boolean }> = ({ children, className = '', delay = 0, style, hover }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${hover ? 'tdp-card-hover' : ''} ${className}`}
    style={style}
  >
    {children}
  </motion.section>
);

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="tdp-section-title">
    <span>{children}</span>
    {right}
  </div>
);

function ProgressRing({ pct, color, size = 46, label }: { pct: number; color: string; size?: number; label: string }) {
  const stroke = 5;
  const r = (size - stroke) / 2 - 1;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--tdp-border)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="tdp-mono" style={{ fontSize: 10, fontWeight: 700, color }}>{pct}%</span>
        <span className="tdp-mono" style={{ fontSize: 6, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--tdp-faint)' }}>{label}</span>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const { showToast, members, projects, addProject, updateProject, deleteProject, isReal, currentMemberInitials, theme } = useTeamDashboard();
  const [mounted, setMounted] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);

  const [repos, setRepos] = useState<GithubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoQuery, setRepoQuery] = useState('');

  const [projectForm, setProjectForm] = useState({
    name: '',
    desc: '',
    repo: '',
    budget: '',
    sprint: 'Sprint 4',
    start: '',
    deadline: '',
    assignedMembers: [] as string[],
  });

  const previousCommitCounts = useRef<Record<string, number>>({});

  useEffect(() => {
    ensureFonts();
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  const teamPool = useMemo(() => {
    if (members.length) return members.map((m) => ({ initials: m.initials, name: m.name }));
    return DEMO_TEAM_POOL;
  }, [members]);

  const filteredRepos = useMemo(() => {
    const q = repoQuery.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter((r) => r.fullName.toLowerCase().includes(q));
  }, [repos, repoQuery]);

  const totalBudget = useMemo(() => projects.reduce((sum, p) => sum + p.budget, 0), [projects]);
  const totalSpent = useMemo(() => projects.reduce((sum, p) => sum + p.spent, 0), [projects]);

  async function loadGithubRepos() {
    setLoadingRepos(true);
    try {
      const res = await githubApiGet<{ success?: boolean; data?: { repositories?: Array<Record<string, unknown>> } }>(`/api/github/repositories?perPage=100`);
      const rawRepos = Array.isArray(res?.data?.repositories) ? res.data.repositories : [];
      const mapped: GithubRepo[] = rawRepos
        .map((repo) => ({
          id: String(repo.id ?? repo.fullName ?? crypto.randomUUID()),
          fullName: String(repo.fullName ?? ''),
          url: String(repo.url ?? ''),
          defaultBranch: typeof repo.defaultBranch === 'string' ? repo.defaultBranch : undefined,
          private: Boolean(repo.private),
        }))
        .filter((repo) => !!repo.fullName);
      setRepos(mapped);
      showToast('GitHub connected', `Fetched ${mapped.length} repositories`, 'success');
    } catch {
      showToast('GitHub', 'GitHub is not connected on your account. Connect GitHub first from Integrations.', 'error');
    } finally {
      setLoadingRepos(false);
    }
  }

  function parseOwnerRepo(fullName: string): { owner: string; repo: string } | null {
    const parts = fullName.split('/').filter(Boolean);
    if (parts.length !== 2) return null;
    return { owner: parts[0], repo: parts[1] };
  }

  async function syncProjectFromGithub(project: Project) {
    const fullName = parseRepoFullName(project.repo);
    if (!fullName) return;
    const ownerRepo = parseOwnerRepo(fullName);
    if (!ownerRepo) return;
    try {
      const recent = await githubApiGet<{ success?: boolean; data?: Array<{ sha?: string; message?: string; date?: string }> }>(
        `/api/github/recent-changes?owner=${encodeURIComponent(ownerRepo.owner)}&repo=${encodeURIComponent(ownerRepo.repo)}&days=7`,
      );
      const commits = Array.isArray(recent?.data) ? recent.data : [];
      const latest = commits[0];
      const previous = previousCommitCounts.current[project.id] ?? project.commitsThisWeek;
      previousCommitCounts.current[project.id] = commits.length;

      await updateProject(project.id, {
        commitsThisWeek: commits.length,
        latestCommitSha: typeof latest?.sha === 'string' ? latest.sha.slice(0, 7) : undefined,
        latestCommitMessage: typeof latest?.message === 'string' ? latest.message.split('\n')[0] : undefined,
        latestCommitAt: typeof latest?.date === 'string' ? latest.date : undefined,
      });

      if (commits.length > previous) {
        showToast('New commits', `${project.name} received ${commits.length - previous} new commit(s)`, 'success');
      }
    } catch {
      /* keep UI resilient */
    }
  }

  useEffect(() => {
    if (!isReal) return;
    const syncAll = () => {
      projects.forEach((project) => {
        void syncProjectFromGithub(project);
      });
    };
    syncAll();
    const interval = setInterval(syncAll, 45_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReal, projects]);

  useEffect(() => {
    if (showCreateProject) {
      if (!projectForm.assignedMembers.length) {
        const fallbackInitials = (members[0]?.initials || currentMemberInitials || '').trim();
        if (fallbackInitials) {
          setProjectForm((prev) => ({ ...prev, assignedMembers: [fallbackInitials] }));
        }
      }
      void loadGithubRepos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateProject]);

  function toggleAssignee(initials: string) {
    setProjectForm((prev) => ({
      ...prev,
      assignedMembers: prev.assignedMembers.includes(initials)
        ? prev.assignedMembers.filter((m) => m !== initials)
        : [...prev.assignedMembers, initials],
    }));
  }

  function pickRepo(repo: GithubRepo) {
    setProjectForm((prev) => ({
      ...prev,
      repo: repo.fullName,
      name: prev.name || repo.fullName.split('/')[1],
    }));
  }

  async function submitCreateProject() {
    const budgetNum = Number(projectForm.budget);
    if (!projectForm.name.trim() || !projectForm.repo.trim() || !projectForm.deadline.trim()) {
      showToast('Create project', 'Project name, repo and deadline are required', 'error');
      return;
    }
    if (!Number.isFinite(budgetNum) || budgetNum <= 0) {
      showToast('Create project', 'Please enter a valid project budget', 'error');
      return;
    }
    if (!projectForm.assignedMembers.length) {
      showToast('Create project', 'Assign at least one team member', 'error');
      return;
    }

    const fullName = parseRepoFullName(projectForm.repo) || projectForm.repo.trim();
    const project: Project = {
      id: `${projectForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`,
      name: projectForm.name.trim(),
      repo: fullName,
      repoUrl: `https://github.com/${fullName}`,
      status: 'active',
      budget: budgetNum,
      spent: 0,
      completionPct: 0,
      sprint: projectForm.sprint,
      deadline: projectForm.deadline,
      commitsThisWeek: 0,
      openPrs: 0,
      members: projectForm.assignedMembers,
      color: '#6e6bff',
    };

    const created = await addProject(project);
    if (!created) {
      showToast('Create project', 'Could not create project in real mode', 'error');
      return;
    }
    showToast('Project created', `${project.name} connected`, 'success');
    setShowCreateProject(false);
    setProjectForm({
      name: '',
      desc: '',
      repo: '',
      budget: '',
      sprint: 'Sprint 4',
      start: '',
      deadline: '',
      assignedMembers: currentMemberInitials ? [currentMemberInitials] : [],
    });
    void syncProjectFromGithub(project);
  }

  async function confirmDeleteProject() {
    if (!deleteTarget) return;
    const project = deleteTarget;
    const removed = await deleteProject(project.id);
    if (!removed) {
      showToast('Remove project', 'Could not remove project in real mode', 'error');
      return;
    }
    showToast('Project removed', `${project.name} deleted`, 'success');
    setDeleteTarget(null);
  }

  const isDark = theme === 'dark';

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        {/* hero */}
        <div className="tdp-hero">
          <div className="tdp-hero-row">
            <div className="tdp-hero-main">
              <div className="tdp-eyebrow">
                <FolderGit2 size={11} />
                Workspace · projects
              </div>
              <h1 className="tdp-title">Projects</h1>
              <p className="tdp-sub">
                {projects.length} active project{projects.length === 1 ? '' : 's'} · track budget, members and commits per project
              </p>
            </div>
            <button className="tdp-btn tdp-btn-primary" onClick={() => setShowCreateProject(true)}>
              <Plus size={13} /> New project
            </button>
          </div>
        </div>

        {/* project cards */}
        {projects.length > 0 && (
          <div className="tdp-proj-grid">
            {projects.map((p, i) => {
              const pct = Math.max(0, Math.min(100, p.completionPct));
              const spentPct = p.budget > 0 ? Math.max(0, Math.min(100, Math.round((p.spent / p.budget) * 100))) : 0;
              const spark = SPARK_PATTERNS[i % SPARK_PATTERNS.length];
              const ringPct = p.status === 'review' ? spentPct : pct;
              const ringLabel = p.status === 'review' ? 'used' : 'done';
              const sColor = statusColor(p.status);
              return (
                <Card key={p.id} hover delay={0.05 + i * 0.05} className="tdp-proj">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <span className="tdp-proj-icon" style={{ background: `${p.color}14`, borderColor: `${p.color}45`, color: p.color }}>
                      <FolderGit2 size={17} />
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="tdp-pill" style={{ background: `${sColor}1a`, color: sColor }}>
                        {statusText(p.status)}
                      </span>
                      <button className="tdp-close" title="Remove project" onClick={() => setDeleteTarget(p)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="tdp-proj-name">{p.name}</div>
                      <div className="tdp-proj-repo">
                        <GitBranch size={9} />
                        {p.repo}
                      </div>
                    </div>
                    <ProgressRing pct={ringPct} color={p.color || T.accent} label={ringLabel} />
                  </div>

                  <div className="tdp-spark">
                    {spark.map((h, si) => (
                      <div key={si} className="tdp-spark-bar" style={{ height: `${h}%`, background: p.color || T.accent, opacity: si === spark.length - 1 ? 1 : 0.35 + si * 0.08 }} />
                    ))}
                  </div>

                  <div className="tdp-proj-meta">
                    <div className="tdp-proj-row">
                      <span>Sprint</span>
                      <span>{p.sprint}</span>
                    </div>
                    <div className="tdp-proj-row">
                      <span>Deadline</span>
                      <span>{p.deadline}</span>
                    </div>
                    <div className="tdp-proj-row">
                      <span>Commits</span>
                      <span style={{ color: p.commitsThisWeek > 0 ? T.green : undefined }}>
                        <GitCommitHorizontal size={9} style={{ verticalAlign: -1.5, marginRight: 2 }} />
                        {p.commitsThisWeek} this week
                      </span>
                    </div>
                    <div className="tdp-proj-row">
                      <span>Open PRs</span>
                      <span>
                        <GitPullRequest size={9} style={{ verticalAlign: -1.5, marginRight: 2 }} />
                        {p.openPrs}
                      </span>
                    </div>
                    {p.latestCommitMessage && (
                      <div className="tdp-proj-row">
                        <span>Latest</span>
                        <span style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.latestCommitMessage}>
                          {p.latestCommitSha ? `${p.latestCommitSha} · ` : ''}
                          {p.latestCommitMessage}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 10, borderTop: '1px solid var(--tdp-border)' }}>
                    <div className="tdp-avstack">
                      {p.members.map((initials) => (
                        <span key={`${p.id}-${initials}`} className="tdp-avatar" style={{ width: 22, height: 22, fontSize: 8, background: `${(MEMBER_COLORS[initials] || T.accent)}25`, color: MEMBER_COLORS[initials] || T.accent, border: '2px solid var(--tdp-solid)' }}>
                          {initials}
                        </span>
                      ))}
                    </div>
                    <button className="tdp-section-link" onClick={() => showToast('Project', `Opening ${p.name}...`)}>
                      View <ArrowUpRight size={10} />
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* create card */}
        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: mounted ? 1 : 0, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="tdp-create"
          onClick={() => setShowCreateProject(true)}
        >
          <span className="tdp-create-icon">
            <FolderPlus size={17} />
          </span>
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--tdp-text)' }}>Create new project</span>
            <span className="tdp-mono" style={{ display: 'block', fontSize: 9.5, color: 'var(--tdp-faint)', marginTop: 2 }}>
              connect a GitHub repo and auto-sync commits
            </span>
          </span>
        </motion.button>

        {/* budget summary */}
        <div className="tdp-section">
          <SectionTitle
            right={
              <span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>
                total ₹{totalSpent.toLocaleString()} / ₹{totalBudget.toLocaleString()}
              </span>
            }
          >
            Project budget summary
          </SectionTitle>
          <Card delay={0.16}>
            <div className="tdp-table-head" style={{ gridTemplateColumns: 'minmax(0,1.8fr) repeat(3,minmax(0,0.8fr)) minmax(0,1.1fr) minmax(0,0.7fr)' }}>
              <span>Project</span>
              <span style={{ textAlign: 'right' }}>Budget</span>
              <span style={{ textAlign: 'right' }}>Used</span>
              <span style={{ textAlign: 'right' }}>Remaining</span>
              <span style={{ textAlign: 'right' }}>Progress</span>
              <span style={{ textAlign: 'right' }}>Status</span>
            </div>
            {projects.map((p) => {
              const usedPct = p.budget > 0 ? Math.round((p.spent / p.budget) * 100) : 0;
              const remaining = Math.max(0, p.budget - p.spent);
              const sColor = statusColor(p.status);
              return (
                <div key={`${p.id}-budget`} className="tdp-table-row" style={{ gridTemplateColumns: 'minmax(0,1.8fr) repeat(3,minmax(0,0.8fr)) minmax(0,1.1fr) minmax(0,0.7fr)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: p.color || T.accent, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tdp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </div>
                  <span className="tdp-table-num">₹{p.budget.toLocaleString()}</span>
                  <span className="tdp-table-num">₹{p.spent.toLocaleString()}</span>
                  <span className="tdp-table-num" style={{ color: remaining > p.budget * 0.25 ? T.green : T.amber }}>
                    ₹{remaining.toLocaleString()}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span className="tdp-progress-track" style={{ flex: 1 }}>
                      <span className="tdp-progress-fill" style={{ width: `${usedPct}%`, background: p.color || T.accent, display: 'block' }} />
                    </span>
                    <span className="tdp-mono" style={{ fontSize: 9.5, color: 'var(--tdp-faint)' }}>{usedPct}%</span>
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    <span className="tdp-pill" style={{ background: `${sColor}1a`, color: sColor }}>
                      {statusText(p.status).toLowerCase()}
                    </span>
                  </span>
                </div>
              );
            })}
            {projects.length === 0 && (
              <div className="tdp-feed-meta" style={{ padding: 26, textAlign: 'center' }}>
                No projects yet — create your first project to start tracking budgets and commits.
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ═══════════ CREATE DRAWER ═══════════ */}
      <AnimatePresence>
        {showCreateProject && (
          <>
            <motion.div className="tdp-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCreateProject(false)} />
            <motion.aside
              className="tdp-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            >
              <div className="tdp-drawer-head">
                <span className="tdp-create-icon" style={{ width: 36, height: 36, borderRadius: 11 }}>
                  <FolderPlus size={15} />
                </span>
                <div>
                  <div className="tdp-drawer-title">Create New Project</div>
                  <div className="tdp-drawer-sub">link a GitHub repo → set budget → assign members</div>
                </div>
                <button className="tdp-close" onClick={() => setShowCreateProject(false)} aria-label="Close">
                  <X size={15} />
                </button>
              </div>

              <div className="tdp-drawer-body">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label className="tdp-label" style={{ margin: 0 }}>GitHub repository picker</label>
                    <button className="tdp-btn tdp-btn-ghost tdp-btn-sm" onClick={() => void loadGithubRepos()} disabled={loadingRepos}>
                      <RefreshCw size={11} style={loadingRepos ? { animation: 'tdpSpin 1s linear infinite' } : undefined} />
                      {loadingRepos ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tdp-faint)' }} />
                    <input className="tdp-input" style={{ paddingLeft: 32 }} placeholder="Search fetched repos…" value={repoQuery} onChange={(e) => setRepoQuery(e.target.value)} />
                  </div>
                  <div className="tdp-repolist">
                    {filteredRepos.length === 0 ? (
                      <div className="tdp-feed-meta" style={{ padding: 14, textAlign: 'center' }}>
                        {loadingRepos ? 'Loading connected repositories…' : 'No repos loaded yet — hit Refresh after connecting GitHub.'}
                      </div>
                    ) : (
                      filteredRepos.map((repo) => (
                        <button type="button" key={repo.id} onClick={() => pickRepo(repo)} className={`tdp-repo-item ${projectForm.repo === repo.fullName ? 'tdp-on' : ''}`}>
                          <GitBranch size={10} style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{repo.fullName}</span>
                          {repo.private && <Lock size={9} style={{ flexShrink: 0, color: 'var(--tdp-faint)' }} />}
                          {projectForm.repo === repo.fullName && <Check size={11} style={{ flexShrink: 0 }} />}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <label className="tdp-label">Project name *</label>
                  <input className="tdp-input" placeholder="e.g. Scrudix Mobile App" value={projectForm.name} onChange={(e) => setProjectForm((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <label className="tdp-label">Description</label>
                  <textarea className="tdp-textarea" rows={2} placeholder="Short project description…" value={projectForm.desc} onChange={(e) => setProjectForm((p) => ({ ...p, desc: e.target.value }))} />
                </div>
                <div>
                  <label className="tdp-label">Repository full name *</label>
                  <input className="tdp-input tdp-mono" placeholder="owner/repo" value={projectForm.repo} onChange={(e) => setProjectForm((p) => ({ ...p, repo: e.target.value }))} />
                </div>

                <div className="tdp-form-2col">
                  <div>
                    <label className="tdp-label">Budget (INR) *</label>
                    <input className="tdp-input" type="number" min={1} placeholder="e.g. 25000" value={projectForm.budget} onChange={(e) => setProjectForm((p) => ({ ...p, budget: e.target.value }))} />
                  </div>
                  <div>
                    <label className="tdp-label">Sprint</label>
                    <div style={{ position: 'relative' }}>
                      <select className="tdp-select" style={{ appearance: 'none' }} value={projectForm.sprint} onChange={(e) => setProjectForm((p) => ({ ...p, sprint: e.target.value }))}>
                        <option>Sprint 4</option>
                        <option>Sprint 5</option>
                        <option>Sprint 6</option>
                      </select>
                      <ChevronDown size={12} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tdp-faint)', pointerEvents: 'none' }} />
                    </div>
                  </div>
                </div>

                <div className="tdp-form-2col">
                  <div>
                    <label className="tdp-label">Start date</label>
                    <input className="tdp-input" type="date" value={projectForm.start} onChange={(e) => setProjectForm((p) => ({ ...p, start: e.target.value }))} />
                  </div>
                  <div>
                    <label className="tdp-label">Deadline *</label>
                    <input className="tdp-input" type="date" value={projectForm.deadline} onChange={(e) => setProjectForm((p) => ({ ...p, deadline: e.target.value }))} />
                  </div>
                </div>

                <div>
                  <label className="tdp-label">Assign team members *</label>
                  <div className="tdp-chips">
                    {teamPool.map((member) => {
                      const selected = projectForm.assignedMembers.includes(member.initials);
                      return (
                        <button key={member.initials} type="button" className={`tdp-chip ${selected ? 'tdp-on' : ''}`} onClick={() => toggleAssignee(member.initials)}>
                          <span className="tdp-avatar" style={{ width: 18, height: 18, fontSize: 7, background: selected ? `linear-gradient(135deg, ${T.accent}, ${T.accent2})` : 'var(--tdp-hover)', color: selected ? '#fff' : 'var(--tdp-dim)' }}>
                            {member.initials}
                          </span>
                          {member.name}
                          {selected && <Check size={10} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="tdp-drawer-foot">
                <button className="tdp-btn tdp-btn-ghost" onClick={() => setShowCreateProject(false)}>
                  Cancel
                </button>
                <button className="tdp-btn tdp-btn-primary" onClick={() => void submitCreateProject()}>
                  Create & Connect <ArrowUpRight size={13} />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════ DELETE CONFIRM ═══════════ */}
      <AnimatePresence>
        {deleteTarget && (
          <>
            <motion.div className="tdp-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDeleteTarget(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="tdp-card"
              style={{ position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%', zIndex: 96, width: 'min(440px, 92vw)', padding: 22 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 11, background: `${T.red}18`, color: T.red }}>
                  <Trash2 size={16} />
                </span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--tdp-text)' }}>Remove Project</div>
                  <div className="tdp-feed-meta">This removes the project and its dashboard data.</div>
                </div>
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--tdp-dim)', lineHeight: 1.6, margin: '4px 0 16px' }}>
                Are you sure you want to remove <b style={{ color: 'var(--tdp-text)' }}>{deleteTarget.name}</b>?
                <span className="tdp-mono" style={{ display: 'block', fontSize: 9.5, color: 'var(--tdp-faint)', marginTop: 4 }}>
                  Repo: {deleteTarget.repo}
                </span>
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="tdp-btn tdp-btn-ghost" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </button>
                <button className="tdp-btn tdp-btn-danger" onClick={() => void confirmDeleteProject()}>
                  <Trash2 size={12} /> Remove project
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
